import { Fragment, type ReactNode } from "react";
import { localizedHref } from "@/lib/localizedHref";

const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"])|([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/gi;

const INTERNAL_PATH_RE = /^\/(?:car|cars|collection|collections|trips|trip|messages|host|dashboard|account|settings|reviews|search|listing|listings|book|owner|renter|inspections|inspection|cancel|reservation|reservations)(?:[/?#]|$)/i;
const INTERNAL_HOSTS = new Set([
  "driveshare.com",
  "www.driveshare.com",
  "bookaclassic.com",
  "www.bookaclassic.com",
]);

function tryParseUrl(raw: string): URL | null {
  try {
    if (raw.startsWith("www.")) return new URL(`https://${raw}`);
    return new URL(raw);
  } catch {
    return null;
  }
}

function isInternalUrl(parsed: URL): boolean {
  if (!parsed) return false;
  if (typeof window !== "undefined" && parsed.host === window.location.host) return true;
  return INTERNAL_HOSTS.has(parsed.host.toLowerCase());
}

function normalizeInternalPath(path: string): string {
  if (!path.startsWith("/")) return `/${path}`;
  return path;
}

interface LinkifiedTextProps {
  text: string;
  className?: string;
}

/**
 * Renders message text with URLs and email addresses converted to clickable
 * links. Internal DRIVESHARE/BookAclassic links open in the same tab and pick
 * up the current locale prefix; everything else opens in a new tab with
 * `rel="noopener noreferrer"`. Plain text segments preserve newlines.
 *
 * Used for both regular user messages and for the bodies of branded
 * `platform_notification` system cards so the same link behaviour applies
 * everywhere in the chat thread.
 */
export function LinkifiedText({ text, className }: LinkifiedTextProps): JSX.Element {
  if (!text) return <span className={className} />;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Reset regex state — module-level regex is sticky across calls otherwise.
  URL_REGEX.lastIndex = 0;
  let key = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const matchStart = match.index;
    const matched = match[0];
    if (matchStart > lastIndex) {
      nodes.push(<Fragment key={`t-${key++}`}>{text.slice(lastIndex, matchStart)}</Fragment>);
    }

    if (match[2]) {
      // Email
      const email = match[2];
      nodes.push(
        <a
          key={`e-${key++}`}
          href={`mailto:${email}`}
          className="break-all underline decoration-current/40 underline-offset-2 hover:decoration-current"
        >
          {email}
        </a>,
      );
    } else {
      const raw = match[1];
      const parsed = tryParseUrl(raw);
      if (parsed) {
        const href = parsed.toString();
        if (isInternalUrl(parsed)) {
          const localized = localizedHref(normalizeInternalPath(parsed.pathname + parsed.search + parsed.hash));
          nodes.push(
            <a
              key={`l-${key++}`}
              href={localized}
              className="break-all underline decoration-current/40 underline-offset-2 hover:decoration-current"
            >
              {raw}
            </a>,
          );
        } else {
          nodes.push(
            <a
              key={`l-${key++}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all underline decoration-current/40 underline-offset-2 hover:decoration-current"
            >
              {raw}
            </a>,
          );
        }
      } else {
        nodes.push(<Fragment key={`t-${key++}`}>{raw}</Fragment>);
      }
    }

    lastIndex = matchStart + matched.length;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`t-${key++}`}>{text.slice(lastIndex)}</Fragment>);
  }
  return <span className={className}>{nodes}</span>;
}
